<template>
  <CardV2 v-if="item" tag="form" class="kanban-pipeline" @submit.prevent="onSubmit">
    <InputComponent v-model="item.label" :label="$t('name')" :placeholder="$t('typeHere') + '...'" required />

    <EditableBoard
      v-model="item.columns"
      :columns="columns"
      @input="setSlugs"
      :create-label="$t('add')"
      :custom-add="add"
      add-fields
      sortable
    >
      <template #label="{ item }">
        <InputComponent v-model.lazy="item.label" @change="() => setSlug(item)" required />
      </template>

      <template #id="{ item, rowIndex }">
        <InputComponent
          :value="rowIndex === 0 ? $t('default') : item.id"
          @input="$ev => rowIndex !== 0 && (item.id = $ev)"
          :disabled="rowIndex === 0"
          required
        />
      </template>

      <template #color="{ item }">
        <ColorSelector v-model="item.color" />
      </template>

      <template #actions="{ item: col }">
        <ButtonComponent
          v-if="item.columns.length > 1"
          icon="fa-pro-light:trash"
          theme="subtle"
          confirm
          @click="removeColumn(col)"
        />
      </template>
    </EditableBoard>

    <InputComponent :label="$t('kanbanUsageCondition')">
      <Radio
        v-model="useCondition"
        :items="[
          { value: false, label: $t('all') },
          { value: true, label: $t('select') },
        ]"
        class="elder-input__element"
      />
    </InputComponent>

    <InputComponent v-if="useCondition">
      <ChecklistComponent
        v-model="condition"
        items="/api/resource/project,page,residentialTemplate?field.label=name&field.value=id"
        :groupBy="{ path: 'resource', label: item => $tc(item.resource, 2) }"
        class="elder-input__element"
      />
    </InputComponent>

    <template #actions>
      <ButtonComponent :label="$t('cancel')" theme="default" @click="$emit('cancel')" />
      <ButtonComponent :label="$t('confirm')" theme="primary" type="submit" :disabled="!isValid" />
    </template>
  </CardV2>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import EditableBoard from '@/components/EditableBoard.vue'
import ChecklistComponent from '@/components/Select/Checklist'
import Radio from '@kvass/radio'
import CardV2 from '@/components/Card-v2.vue'
import ColorSelector from '@kvass/custom-fields/src/Fields/ColorSelector'
import { Slugify, Clone } from '@/utils'

const ColumnTemplate = {
  id: '',
  label: '',
  color: '#4a90e2',
}

export default {
  props: {
    pipeline: {
      type: Object,
      required: true,
    },
  },
  watch: {
    pipeline: {
      handler: 'init',
      immediate: true,
    },
    useCondition() {
      if (this.useCondition) return
      this.condition = null
    },
  },
  data() {
    return {
      item: null,
      useCondition: false,
    }
  },
  computed: {
    columns() {
      return [
        { id: 'color', label: this.$t('color'), size: '60px' },
        { id: 'label', label: this.$t('status'), size: '1fr' },
        { id: 'id', label: 'ID', size: '1fr' },
        { id: 'actions', size: '45px' },
      ].filter(Boolean)
    },
    isValid() {
      return this.item.label && this.item.columns.every((c, i) => (i === 0 ? true : c.id !== null) && c.label)
    },
    condition: {
      get() {
        return this.$path('condition.id.$in', this.item) || []
      },
      set(val) {
        if (!val || !val.length) return (this.item.condition = null)
        return (this.item.condition = { id: { $in: val } })
      },
    },
  },
  methods: {
    add() {
      this.item.columns.push(structuredClone(ColumnTemplate))
    },
    setSlugs() {
      this.item.columns.forEach(column => {
        if (column.id) return
        this.setSlug(column)
      })
    },
    setSlug(item) {
      if (item.id) return (item.id = null)

      const slug = Slugify(item.label)

      let conflictCounter = 0
      while (this.item.columns.find(c => c.id === (conflictCounter ? [slug, conflictCounter].join('-') : slug))) {
        conflictCounter++
      }

      item.id = conflictCounter ? [slug, conflictCounter].join('-') : slug
    },
    removeColumn(columnToRemove) {
      this.item.columns = this.item.columns.filter(col => col.id !== columnToRemove.id)
    },
    init() {
      if (!this.pipeline) return

      this.item = Clone(this.pipeline)

      if (this.item.condition) this.useCondition = true
      if (!this.item.columns.length) this.add()
    },
    onSubmit() {
      this.item.columns[0].id = null
      this.$emit('save', this.item)
    },
  },
  components: {
    InputComponent,
    EditableBoard,
    ChecklistComponent,
    Radio,
    CardV2,
    ColorSelector,
  },
}
</script>

<style lang="scss">
.kanban-pipeline {
  width: 800px;

  & > .card-v2__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .editable-board__cell input {
    padding: 0.7em 0.8em;
  }

  .elder-input__field:has(input[type='color']) label {
    display: none;
  }

  input[type='color'] {
    height: 45px !important;
    padding: 0.3em 0.8em;
  }
}
</style>
